// A MAC order (plan omni-rick-round 3.1 and 7C, mock M6 mac-orders.html): ops start from the support
// ticket where the tech recorded the work, keep or leave out each of its charges, add the monthly
// services, pick the term and mark it sold. These are the screen's rules as pure functions, so the
// tests and the screen read the same answers: each ticket line's state, what is left to sort out
// before it can be sold, the totals, and the quote POST /api/quotes receives.
import {
  isBillable,
  type CatalogProductV2,
  type Cents,
  type ContractTerm,
  type CustomerSummary,
  type CustomerTicketSummary,
  type IsBillableResult,
  type MacLineCandidate,
  type QuoteInput,
} from "@phoneware/lens-domain";

/**
 * A support ticket as GET /api/customers/:id/tickets answers it (the server's CustomerTicketSummary).
 */
export type SupportTicket = CustomerTicketSummary;

/** Autotask's Complete ticket status (plan 3.4). */
export const TICKET_COMPLETE = 5;

/** The two terms a MAC order takes: it adds to the customer's account, so it has no term of its own or ends with theirs. */
export type MacTerm = Extract<ContractTerm, "month_to_month" | "coterminous">;
export const MAC_TERMS: MacTerm[] = ["month_to_month", "coterminous"];
export const MAC_TERM_WORD: Record<MacTerm, string> = { month_to_month: "No contract", coterminous: "Coterminous" };

/** A ticket charge on the screen: the server's candidate, the product ops picked for one Omni could not match, and whether it is left out. */
export interface MacLine {
  candidate: MacLineCandidate;
  picked: CatalogProductV2 | null;
  out: boolean;
}

/** A monthly service ops added from the catalog. */
export interface MacService {
  product: CatalogProductV2;
  qty: number;
}

/** ok: it goes on the order. bad: matched, but not billable. open: not in the catalog and nothing picked. out: left out. */
export type LineState = "ok" | "bad" | "open" | "out";

/** The catalog product a line bills as: the one ops picked, else the one the charge matched. */
export function lineProduct(l: MacLine): CatalogProductV2 | null {
  if (l.picked) return l.picked;
  return l.candidate.state === "matched" ? ((l.candidate.product as CatalogProductV2 | null | undefined) ?? null) : null;
}

/** Whether the line's product can be billed: the server's answer for a matched charge, the same rule for a picked product. */
export function lineBillable(l: MacLine): IsBillableResult | null {
  const p = lineProduct(l);
  if (!p) return null;
  if (l.picked) return isBillable(p);
  return l.candidate.billable ?? isBillable(p);
}

export function lineState(l: MacLine): LineState {
  if (l.out) return "out";
  const billable = lineBillable(l);
  if (!billable) return "open";
  return billable.ok ? "ok" : "bad";
}

export function reasonOf(result: IsBillableResult | null): string {
  return result && !result.ok ? result.reason : "";
}

/** What still stops it being sold, each tied to its ticket line when it has one. */
export function macProblems(lines: MacLine[], services: MacService[]): Array<{ line: number | null; text: string }> {
  const out: Array<{ line: number | null; text: string }> = [];
  lines.forEach((l, i) => {
    const state = lineState(l);
    if (state === "bad") out.push({ line: i, text: `${lineProduct(l)?.name ?? l.candidate.name} isn\u2019t billable: ${reasonOf(lineBillable(l))}. Leave it out first.` });
    if (state === "open") out.push({ line: i, text: `${l.candidate.chargeName} isn\u2019t in the catalog. Pick a product or leave it out.` });
  });
  if (!out.length && !lines.some((l) => lineState(l) === "ok") && !services.length) {
    out.push({ line: null, text: "Nothing to sell yet. Keep a line from the ticket or add a monthly service." });
  }
  return out;
}

/** Monthly and one-time totals at the catalog's list prices, which is what the order bills. */
export function macTotals(lines: MacLine[], services: MacService[]): { monthly: Cents; oneTime: Cents } {
  let monthly = 0;
  let oneTime = 0;
  for (const l of lines) {
    const p = lineProduct(l);
    if (!p || lineState(l) !== "ok") continue;
    if (p.billing === "monthly") monthly += p.listPrice * l.candidate.qty;
    else oneTime += p.listPrice * l.candidate.qty;
  }
  for (const s of services) monthly += s.product.listPrice * s.qty;
  return { monthly, oneTime };
}

/**
 * The quote POST /api/quotes (or PUT, on a retry) receives: a change quote with its source ticket, priced at list,
 * one line per product. Charges and services that bill as the same product add up.
 */
export function macQuoteInput(customerId: number, ticket: SupportTicket | { id: number; ticketNumber: string; title?: string; status?: number }, term: MacTerm, lines: MacLine[], services: MacService[]): QuoteInput {
  const merged = new Map<string, { productId: string; qty: number; unitPrice: Cents }>();
  const add = (p: CatalogProductV2, qty: number) => {
    const line = merged.get(p.id);
    if (line) line.qty += qty;
    else merged.set(p.id, { productId: p.id, qty, unitPrice: p.listPrice });
  };
  for (const l of lines) {
    const p = lineProduct(l);
    if (p && lineState(l) === "ok") add(p, l.candidate.qty);
  }
  for (const s of services) add(s.product, s.qty);
  return {
    customerId,
    type: "change",
    term,
    lines: [...merged.values()],
    pdfNotes: "",
    internalNotes: "",
    approvalNote: null,
    contactName: null,
    contactEmail: null,
    source: { kind: "autotask_ticket", ticketId: ticket.id, ticketNumber: ticket.ticketNumber },
  };
}

/** A MAC order adds to the customer's OneBill account, so the customer needs one linked. */
export function customerReady(c: Pick<CustomerSummary, "billing"> | null): boolean {
  return c?.billing.state === "linked";
}

/** The OneBill account a linked customer bills under. */
export function onebillAccount(c: Pick<CustomerSummary, "billing"> | null): string | null {
  return c?.billing.state === "linked" ? c.billing.accountNumber : null;
}

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

/** Customers whose name or account number holds every word typed, by name. */
export function searchCustomers(customers: CustomerSummary[], q: string): CustomerSummary[] {
  const words = norm(q).split(" ").filter(Boolean);
  return customers
    .filter((c) => {
      const hay = norm(`${c.name} ${c.accountNumber ?? ""} ${onebillAccount(c) ?? ""}`);
      return words.every((w) => hay.includes(w));
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Catalog items whose name, category or code holds every word typed: billable ones first, then by name. Nothing until something is typed. */
export function searchCatalog(catalog: CatalogProductV2[], q: string, monthlyOnly: boolean): CatalogProductV2[] {
  const words = norm(q).split(" ").filter(Boolean);
  if (!words.length) return [];
  return catalog
    .filter((p) => (!monthlyOnly || p.billing === "monthly") && words.every((w) => norm(`${p.name} ${p.category} ${p.code}`).includes(w)))
    .sort((a, b) => Number(isBillable(b).ok) - Number(isBillable(a).ok) || a.name.localeCompare(b.name));
}

/** The status as Autotask names it; without the name, Complete or Open for anything Autotask hasn't closed. */
export function ticketStatusWord(t: Pick<SupportTicket, "status" | "statusName">): string {
  return t.statusName || (t.status === TICKET_COMPLETE ? "Complete" : "Open");
}
